



// someting that can be done in an application, a capability.
// associated with a user, models the right to trigger the action.

import { ActionTemplate, any, do_anything, getActions } from 'apprise-core/action'
import { guest } from 'apprise-core/auth'
import { manageTenant, noTenant } from 'apprise-core/tenant'
import { User } from 'apprise-core/user'

export class AuthzError extends Error { }

export type Oracle = ReturnType<ReturnType<typeof makeOracle>['given']>


export type ActionScope = 'direct' | 'indirect'

// returns an authz oracle for arbitrary users.
export const makeOracle = () => {

    const actions = getActions()

    const assert = (outcome: boolean) => {

        if (!outcome)
            throw new AuthzError("just can't do that.")

        return outcome

    }

    return {

        given: (user: User) => {

            const perms = user?.permissions ?? []


            const oracle = {

                user: () => user,


                isAdmin: () => oracle.can(do_anything)

                ,


                // cf. documentation on AuthzOracle.
                can: (action: ActionTemplate, tenantScope = user.tenant) => {

                    // passthrough rules: tenant managers can do anything in the scope of that tenant.
                    // even if they don't have the specific permissions, they're like global admins there.
                    // these include multi-tenant managers like regional admins and multimanagers.
                    // but it applies only for actions over tenant resources.
                    // so it doesn't apply to actions that are only admin, or to actions over tenant themselves.
                    const passthrough = action.actionType !== 'admin' && !actions.isLike(action,manageTenant) && (

                        // but what if there is no tenant in scope? 
                        // this happens with regional admins and actions like "add resource":
                        // 1. we can't get a tenant from the user (it's tenantless). 
                        // 2. we can't get one from the resource yet (we haven't created it yet). 
                        // Yet we shouldn't block the regional admin: so we check that the user manages at least some tenant, ie. the 
                        // action is 'plausible'. This is unless the action is itself "manage tenant", or we will prevent multi-managers  
                        // BUT the application will need to guard the subsequent actions of the admin, eg. filter out tenants it doesn't manage. 

                        tenantScope === any ?

                            oracle.canForSome(manageTenant) 
                            :
                            actions.impliedByAny(actions.specialise(manageTenant, tenantScope), perms)
                        
                    )

                    return passthrough || actions.impliedByAny(action, perms)

                }

                ,


                canAny: (actions: ActionTemplate[], tenantScope?: string) => actions.some(a => oracle.can(a, tenantScope))

                ,


                canAll: (actions: ActionTemplate[], tenantScope?: string) => !actions.some(a => !oracle.can(a, tenantScope))

                ,


                canForSome: (action: ActionTemplate) => actions.matchAny(action, perms)



            }

            const tenancy = {

                // whether a single action/template or a whole module thereof are intended for the current user.
                isFor: ({actionType}: {actionType?: string}) => {

                    switch(actionType) {

                        case 'admin': return tenancy.hasNoTenant()
                        case 'tenant': return tenancy.isTenantUser()
                        default: return true
                    }

                }

                ,

                // most basic tenancy question, avoids having to known the target user.
                tenant: () => user.tenant

                ,


                hasNoTenant: () => user.tenant === noTenant || !user.tenant   // cover unexpected case too: tenant undefined

                ,

                isCrossTenant: () => tenancy.hasNoTenant() || tenancy.isMultiManager()

                ,


                isTenantUser: () => !tenancy.hasNoTenant()

                ,


                // manages its own tenant, directly.
                isTenantManager: () => tenancy.isTenantUser() && tenancy.isManagerOf(tenancy.tenant(), 'direct')


                ,

                isRegularTenantUser: () => tenancy.isTenantUser() && !tenancy.managesSomeTenant()

                ,

                // has a tenant and operates only in that tenant (with management rights or less).
                isSingleTenantUser: () => tenancy.isTenantUser() && !tenancy.isMultiManager()


                ,

                // a tenant user that manages multiple tenants other than its own. 
                isMultiManager: () => tenancy.isTenantUser() && tenancy.managesMultipleTenants('direct')

                ,

                isGuest: () => user.username === guest.username

                ,

                //  ---------------------------------------------------------------------------------------------- tenancy-based privilege checks.


                /** manages a given tenant, either directly or indirectly (as an implication). */
                isManagerOf: (tenant: string, mode: ActionScope = 'indirect') => {

                    if (!tenant || tenant === any)
                        return false       // corner case. there's no tenant to manage

                    const manageIt = actions.specialise(manageTenant, tenant)

                    return mode === 'indirect' ?

                        oracle.can(manageIt, tenant)   // is implied (standard)

                        :

                        actions.isAny(manageIt, user.permissions)  // exists one that is exactly it.

                }

                ,



                /** manages at least one tenant, possibly his own, either directly or indirectly (as an implication). */
                managesSomeTenant: (mode: ActionScope = 'indirect') =>

                    mode === 'indirect' ? oracle.canForSome(manageTenant) : actions.matchAny(manageTenant, user.permissions)

                ,


                /** manages at least one tenant other than his own (and possibly all). */
                managesMultipleTenants: (mode: ActionScope = 'indirect') =>

                    // manages other tenants directly.
                    actions.matchAnyOther(actions.specialise(manageTenant, user.tenant), user.permissions)

                    ||

                    // or has no tenant and can manage them all, indirectly.
                    (mode === 'indirect' && tenancy.hasNoTenant() && oracle.can(manageTenant))

                ,

                //** all the tenants directly managed. */
                managedTenants: () => user.permissions.filter(p => p.resource && p.resource !== any && actions.isLike(p, manageTenant)).map(a => a.resource!)
           
            }

            

            const assertions = {

                assertIsAdmin: (...args: Parameters<typeof oracle.isAdmin>) => assert(oracle.isAdmin(...args))

                ,

                assertCan: (...args: Parameters<typeof oracle.can>) => assert(oracle.can(...args))

                ,

                assertCanAny: (...args: Parameters<typeof oracle.canAny>) => assert(oracle.canAny(...args))

                ,
                assertCanAll: (...args: Parameters<typeof oracle.canAll>) => assert(oracle.canAll(...args))

                ,

                assertCanForSome: (...args: Parameters<typeof oracle.canForSome>) => assert(oracle.canForSome(...args))


            }



            return { ...oracle, ...tenancy, ...assertions }
        }
    }

}
